/**
 * Hook for admin challenge detail view and editing.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPatch, apiPost } from "../lib/api";
import type { DisciplineSlug, ContentReviewStatus } from "@jumpbean/shared";

// ----- Types -----

export interface ChallengeDetail {
  id: string;
  levelId: string;
  levelNumber: number;
  levelName: string;
  disciplineId: string;
  disciplineSlug: DisciplineSlug;
  disciplineName: string;
  challengeNumber: number;
  type: string;
  content: Record<string, unknown>;
  difficulty: number;
  isActive: boolean;
  skillTags: string[];
  aiMetadata: AiMetadata | null;
  reviewStatus: ContentReviewStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export interface AiMetadata {
  model_used?: string;
  generated_at?: string;
  agent_version?: string;
  prompt_summary?: string;
  generation_run_id?: string;
}

interface ChallengeDetailResponse {
  success: boolean;
  data: ChallengeDetail;
}

interface UpdateChallengeRequest {
  challengeNumber?: number;
  type?: string;
  content?: Record<string, unknown>;
  difficulty?: number;
  skillTags?: string[];
  reviewStatus?: ContentReviewStatus;
  reviewNotes?: string | null;
  isActive?: boolean;
}

interface UpdateChallengeResponse {
  success: boolean;
  data: ChallengeDetail;
}

interface RegenerateResponse {
  success: boolean;
  data: {
    challenges: Array<{
      content: Record<string, unknown>;
      skillTags: string[];
    }>;
  };
}

interface AutoTagResponse {
  success: boolean;
  data: {
    suggestedSkills: string[];
  };
}

// ----- Hook -----

export function useAdminChallengeDetail(challengeId: string | undefined) {
  const [challenge, setChallenge] = useState<ChallengeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Fetch challenge details
  const fetchChallenge = useCallback(async () => {
    if (!challengeId) {
      setChallenge(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await apiGet<ChallengeDetailResponse>(
        `/api/admin/challenges/${challengeId}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data?.data) {
        setChallenge(result.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch challenge:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load challenge"
      );
      setChallenge(null);
    } finally {
      setIsLoading(false);
    }
  }, [challengeId]);

  // Initial fetch
  useEffect(() => {
    fetchChallenge();
  }, [fetchChallenge]);

  // Update challenge
  const updateChallenge = useCallback(
    async (updates: UpdateChallengeRequest) => {
      if (!challengeId) {
        return { success: false, error: "No challenge ID" };
      }

      setIsSaving(true);
      setSaveError(null);

      try {
        const result = await apiPatch<UpdateChallengeResponse>(
          `/api/admin/challenges/${challengeId}`,
          updates
        );

        if (result.error) {
          throw new Error(result.error.message);
        }

        if (result.data?.data) {
          setChallenge(result.data.data);
        }

        return { success: true };
      } catch (err) {
        console.error("Failed to update challenge:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update challenge";
        setSaveError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsSaving(false);
      }
    },
    [challengeId]
  );

  // Update review status (convenience method)
  const updateReviewStatus = useCallback(
    async (status: ContentReviewStatus, notes?: string) => {
      return updateChallenge({
        reviewStatus: status,
        reviewNotes: notes ?? null,
      });
    },
    [updateChallenge]
  );

  // Regenerate challenge content with AI
  const regenerateWithAI = useCallback(async () => {
    if (!challenge) {
      return { success: false, error: "No challenge loaded" };
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const result = await apiPost<RegenerateResponse>(
        "/api/admin/ai/generate",
        {
          discipline: challenge.disciplineSlug,
          level: challenge.levelNumber,
          challengeType: challenge.type,
          count: 1,
          customInstructions: `Regenerate and improve this existing challenge. Current content: ${JSON.stringify(challenge.content)}`,
        }
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      if (result.data?.data?.challenges?.[0]) {
        const newChallenge = result.data.data.challenges[0];
        return {
          success: true,
          content: newChallenge.content,
          skillTags: newChallenge.skillTags,
        };
      }

      return { success: false, error: "No content generated" };
    } catch (err) {
      console.error("Failed to regenerate with AI:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to regenerate content";
      setSaveError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [challenge]);

  // Auto-tag skills using AI (simulated - uses validation endpoint)
  const autoTagSkills = useCallback(async () => {
    if (!challenge) {
      return { success: false, error: "No challenge loaded", suggestedSkills: [] };
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // For now, we'll use the generate endpoint with count 0 to just get skill suggestions
      // In a real implementation, this would call a dedicated auto-tag endpoint
      const result = await apiPost<AutoTagResponse>(
        "/api/admin/ai/validate",
        {
          discipline: challenge.disciplineSlug,
          challengeType: challenge.type,
          content: challenge.content,
        }
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      // Since we don't have a dedicated auto-tag endpoint, suggest skills based on challenge type
      // This is a simplified implementation
      const suggestedSkills = getSuggestedSkillsForType(
        challenge.disciplineSlug,
        challenge.type,
        challenge.levelNumber
      );

      return {
        success: true,
        suggestedSkills,
      };
    } catch (err) {
      console.error("Failed to auto-tag skills:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to auto-tag skills";
      setSaveError(errorMessage);
      return { success: false, error: errorMessage, suggestedSkills: [] };
    } finally {
      setIsSaving(false);
    }
  }, [challenge]);

  return {
    challenge,
    isLoading,
    error,
    isSaving,
    saveError,
    updateChallenge,
    updateReviewStatus,
    regenerateWithAI,
    autoTagSkills,
    refetch: fetchChallenge,
  };
}

// ----- Helper Functions -----

/**
 * Get suggested skills based on challenge type and level.
 * This is a simplified implementation - in production, this would be AI-powered.
 */
function getSuggestedSkillsForType(
  discipline: DisciplineSlug,
  challengeType: string,
  level: number
): string[] {
  const suggestions: Record<DisciplineSlug, Record<string, string[]>> = {
    math: {
      counting: level <= 2 ? ["counting_1_5", "counting_6_10"] : ["counting_11_20"],
      number_recognition: ["number_recognition"],
      addition: level <= 4 ? ["addition_sums_5"] : ["addition_sums_10"],
      subtraction: level <= 4 ? ["subtraction_from_5"] : ["subtraction_from_10"],
      comparison: ["comparison_more_less", "comparison_equal"],
    },
    spelling: {
      letter_recognition: ["letter_recognition_uppercase", "letter_recognition_lowercase"],
      beginning_sound: ["beginning_sounds_consonants", "beginning_sounds_vowels"],
      word_building: ["cvc_words", "vowel_sounds_short"],
    },
    patterns: {
      pattern_sequence: ["pattern_ab", "pattern_completion"],
      color_pattern: ["pattern_colors", "pattern_ab"],
    },
    typing: {
      key_finding: ["keyboard_familiarity", "key_finding_letters"],
      sequence_typing: ["letter_sequences"],
      word_typing: ["simple_words", "cvc_word_typing"],
    },
    programming: {
      single_command: ["directional_commands", "single_command_execution"],
      maze: level <= 3 ? ["maze_simple"] : ["maze_complex", "sequential_commands"],
    },
  };

  return suggestions[discipline]?.[challengeType] || [];
}
